import { Media, Meta } from '@travelwin/cms/types';
import type { MetaDescriptor } from 'react-router';

import { Route as RootRoute } from '../../app/+types/root';
import { EcommerceConfig } from '../interfaces';
import { getMediaUrlForSize } from './mediaUtils';
import { getSeoLinks } from './seoUtils';
import { getPathnameWithoutLocale } from './urlUtils';

export class MetaStack {
  private stack: MetaDescriptor[];

  constructor(stack: MetaDescriptor[] = []) {
    this.stack = stack;
  }

  isOverridableMeta(descriptor: MetaDescriptor) {
    if ('charSet' in descriptor) return true;
    if ('title' in descriptor) return true;
    if ('name' in descriptor) return true;
    if ('property' in descriptor) return descriptor.property === 'og:image';

    return false;
  }

  getUniqueKey(descriptor: MetaDescriptor): string {
    // We use the 'in' operator for type narrowing to correctly identify the descriptor's type.
    if ('charSet' in descriptor) {
      return 'charSet';
    }
    if ('title' in descriptor) {
      return 'title';
    }
    if ('name' in descriptor) {
      return `name:${descriptor.name}`;
    }
    if ('property' in descriptor) {
      return `property:${descriptor.property}`;
    }
    if ('httpEquiv' in descriptor) {
      return `httpEquiv:${descriptor.httpEquiv}`;
    }
    if ('script:ld+json' in descriptor) {
      return 'script:ld+json';
    }
    // As a fallback, we stringify it to ensure a unique key.
    return JSON.stringify(descriptor);
  }

  push(meta: MetaDescriptor) {
    const isOverridable = this.isOverridableMeta(meta);
    if (!isOverridable) {
      this.stack.push(meta);
      return;
    }

    const filteredItems = this.stack.filter((item) => {
      const itemKey = this.getUniqueKey(item);
      const metaKey = this.getUniqueKey(meta);
      return itemKey !== metaKey;
    });

    filteredItems.unshift(meta);
    this.stack = filteredItems;
  }

  title({ title, brandName }: { title?: string | null; brandName?: string }) {
    if (!title) return;
    const metaTitle = brandName ? `${title} | ${brandName}` : `${title}`;
    this.push({ title: metaTitle });
  }

  description(description?: string | null) {
    if (!description) return;
    this.push({ name: 'description', content: description });
  }

  image(image?: string | number | Media | undefined | null) {
    if (!image) return;

    const content =
      typeof image === 'string' ? image : getMediaUrlForSize(image, 'w1080');

    this.push({
      property: 'og:image',
      content,
    });
  }

  structuredData(
    jsonld?:
      | string
      | number
      | boolean
      | { [k: string]: unknown }
      | unknown[]
      | null
      | undefined,
  ) {
    if (jsonld) {
      this.push({
        'script:ld+json': jsonld,
      });
    }
  }

  get() {
    return this.stack;
  }
}

type RootMatch = {
  id: 'root';
  meta: MetaDescriptor[];
  data: { config: EcommerceConfig };
};

type AnyMatch =
  | {
      id: string;
    }
  | undefined;

type MetaMatches = (RootMatch | AnyMatch)[];

type MetaData = { meta: Meta } | undefined;

export const metaWithCMSDefaultsNoIndexAllowed = ({
  matches,
  data,
}: {
  matches: MetaMatches;
  data: MetaData;
}): MetaDescriptor[] => {
  if (!data) return [];

  // We'll use root meta tags definition as default.
  // If there's some tag missing, we'll use root's.
  const rootMatch = matches?.find((match) => match?.id === 'root') as RootMatch;
  const rootMetaDefaults = rootMatch.meta;

  const metaStack = new MetaStack(rootMetaDefaults);

  // Override root defaults with specific CMS meta tag
  // configuration for the page if each exists
  const { title, description, image, hideFromSitemap, structuredData } =
    data.meta;

  const brandName = rootMatch.data.config.BRAND_NAME;

  metaStack.title({ title, brandName });

  metaStack.description(description);

  metaStack.image(image);

  structuredData?.forEach(({ jsonld }) => {
    metaStack.structuredData(jsonld);
  });

  if (hideFromSitemap) metaStack.push({ name: 'robots', content: 'noindex' });

  return metaStack.get();
};

export const metaWithCMSDefaults = ({
  matches,
  data,
}: {
  matches: MetaMatches;
  data: MetaData;
}): MetaDescriptor[] => {
  const dataOrEmpty = data ?? { meta: {} };
  const alwaysIndex: MetaData = {
    ...{
      ...dataOrEmpty,
      meta: { ...dataOrEmpty?.meta, hideFromSitemap: false },
    },
  };

  return metaWithCMSDefaultsNoIndexAllowed({ matches, data: alwaysIndex });
};

export const metaRootPath: RootRoute.MetaFunction = ({ data, location }) => {
  if (!data) return [];

  const { config, requestUrl, meta, locale } = data;
  const { title, description, image, structuredData } = meta;
  const pathname = getPathnameWithoutLocale(location.pathname);

  const metaStack = new MetaStack();

  metaStack.title(
    title
      ? { title, brandName: config.BRAND_NAME }
      : { title: config.BRAND_NAME },
  );

  metaStack.description(description);

  metaStack.image(image);

  structuredData?.forEach(({ jsonld }) => {
    metaStack.push({
      'script:ld+json': jsonld,
    });
  });

  return [...metaStack.get(), ...getSeoLinks({ requestUrl, pathname, locale })];
};
